/**
 * The fleet of unattended jobs, and how to tell whether one is healthy.
 *
 * This list is deliberately *declared* rather than derived from what has
 * written to Firestore. A status page built from whatever documents happen to
 * exist has a hole in exactly the shape of the problem it is meant to catch: a
 * job that has never once run writes nothing, contributes no row, and leaves a
 * clean screen. Naming the fleet up front turns that into a visible absence.
 *
 * Nothing here talks to Firestore. It takes the runs and the current time and
 * returns a verdict, so the page and the header indicator cannot disagree, and
 * so the interesting cases — never ran, ran and died, ran and lied — are
 * testable without an emulator.
 *
 * The Go side of this pairing is timing/internal/jobrun.
 */

/** How a job comes to be running at all. */
export type JobKind =
  /** Cloud Scheduler starts it on a cron. Silence past its cadence is a fault. */
  | 'scheduled'
  /** The reconciler starts it for a capture window. Silence is normal. */
  | 'onDemand'

export interface JobSpec {
  /** The Cloud Run job name, and the `job` field on every run it writes. */
  id: string
  label: string
  /** One line, in the terms someone debugging at 8am would use. */
  what: string
  kind: JobKind
  /** Cron, for scheduled jobs. The single source for the expected cadence. */
  schedule?: string
  /** The Cloud Scheduler job name, so drift from the deploy script is testable. */
  scheduler?: string
  /**
   * How often a long-running job is expected to beat while it works. Independent
   * of the cadence: an on-demand job has no cadence but still has a pulse.
   */
  heartbeatSeconds?: number
}

/**
 * Every job that runs without anybody asking.
 *
 * Order is the order the status page shows them, which is roughly the order
 * they matter: the reconciler decides whether anything happens at all, and the
 * two it starts are the ones actually carrying race data.
 */
export const JOBS: JobSpec[] = [
  {
    id: 'reconcile',
    label: 'Reconciler',
    what: 'Decides what should be capturing, and starts it. Everything else follows from this one.',
    kind: 'scheduled',
    schedule: '*/5 * * * *',
    scheduler: 'reconcile-every-5m',
  },
  {
    id: 'ris-capture',
    label: 'RIS capture',
    what: 'Records the RIS channels to the raw snapshot log. Started per capture window.',
    kind: 'onDemand',
    heartbeatSeconds: 30,
  },
  {
    id: 'interpret',
    label: 'Interpret',
    what: 'Turns raw snapshots into sessions, laps, standings and stints.',
    kind: 'onDemand',
    heartbeatSeconds: 30,
  },
  {
    id: 'tsl-capture',
    what: 'Records a TSL event from its live socket, plus the lap history that only exists while a session runs.',
    label: 'TSL capture',
    kind: 'onDemand',
    heartbeatSeconds: 30,
  },
  {
    id: 'tsl-interpret',
    label: 'TSL interpret',
    what: 'Turns captured TSL messages and lap history into sessions, laps and stints.',
    kind: 'onDemand',
    heartbeatSeconds: 30,
  },
  {
    id: 'fuel',
    label: 'Fuel',
    what: 'Reads refuelling from the venue’s fuel station and matches each fill to a pit stop. Started per capture window, for events with a fuel source.',
    kind: 'onDemand',
    heartbeatSeconds: 30,
  },
  {
    id: 'ris-probe',
    label: 'RIS probe',
    what: 'Sweeps all seventeen RIS channels for evidence of what is live. Captures nothing.',
    kind: 'scheduled',
    schedule: '*/5 * * * *',
    scheduler: 'ris-probe-every-5m',
  },
  {
    id: 'raw-archive',
    label: 'Raw archive',
    what: 'Moves captured raw snapshots older than a week into Cloud Storage, then frees them from Firestore.',
    kind: 'scheduled',
    schedule: '0 4 * * *',
    scheduler: 'raw-archive-nightly',
  },
  {
    id: 'tsl-diary',
    label: 'TSL diary',
    what: 'Scrapes the TSL diary into the event catalogue.',
    kind: 'scheduled',
    schedule: '0 3 * * *',
    scheduler: 'tsl-diary-nightly',
  },
]

/** One recorded execution, as the admin reads it back. */
export interface JobRun {
  id: string
  job: string
  execution: string
  windowId?: string
  status: 'running' | 'ok' | 'failed'
  startedAt: Date
  heartbeatAt: Date
  /** Absent while still running. */
  finishedAt?: Date | null
  summary?: string
  error?: string
  /**
   * Which build of the timing image this run is executing.
   *
   * A Cloud Run Job pins its image when the execution starts, so a job running
   * for hours is running whatever was deployed when it began. Absent on runs
   * recorded before this was carried.
   */
  build?: string
  counters?: Record<string, number>
  notes?: string[]
}

/**
 * Seconds between runs implied by a cron expression.
 *
 * Only the two shapes actually in use, and null for anything else. A general
 * cron parser would be a lot of code in service of expressions nobody has
 * written — and returning null is honest, where guessing would quietly produce
 * a staleness threshold that is wrong in one direction or the other.
 */
export function intervalSeconds(cron: string | undefined): number | null {
  if (!cron) return null
  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5) return null
  const [min = '', hour = '', dom = '', mon = '', dow = ''] = parts
  if (dom !== '*' || mon !== '*' || dow !== '*') return null

  const everyN = /^\*\/(\d+)$/.exec(min)
  if (everyN && hour === '*') return Number(everyN[1]) * 60
  if (/^\d+$/.test(min) && /^\d+$/.test(hour)) return 24 * 60 * 60
  return null
}

/**
 * How late a scheduled job may be before it counts as overdue.
 *
 * Three missed runs rather than one. A single miss is ordinary — a scheduler
 * retry, a cold start, a deploy — and a status page that goes red for those
 * teaches the reader to ignore it, which costs more than the miss did. Three in
 * a row is a fault.
 */
const lateAfter = 3

/**
 * How long past its heartbeat a running job is presumed dead.
 *
 * Generous for the same reason, and because the alternative reading — showing a
 * job as alive when it stopped twenty minutes ago — is the failure this whole
 * exercise exists to prevent.
 */
const stalledAfter = 4

export type JobState =
  /** Ran, recently enough, and said it succeeded. */
  | 'ok'
  /** Running now, and beating. */
  | 'running'
  /** On-demand, and nothing has needed it. Not a fault. */
  | 'idle'
  /** Scheduled, and has not run within its cadence. */
  | 'late'
  /** Says it is running, but stopped beating. The process died mid-run. */
  | 'stalled'
  /** Ran and reported failure. */
  | 'failed'
  /** Scheduled, and has never written anything at all. */
  | 'never'

export interface JobHealth {
  spec: JobSpec
  state: JobState
  /** Worth showing prominently — anything a person should act on. */
  bad: boolean
  /** A short human reason, already phrased for display. */
  detail: string
  latest: JobRun | null
}

const BAD: JobState[] = ['late', 'stalled', 'failed', 'never']

/**
 * Judges one job from its most recent run.
 *
 * The order of the checks is the point. "Says it is running" is tested against
 * the heartbeat *before* it is believed, because a job that died without
 * finishing leaves a document claiming it is still going, and that document is
 * the most misleading thing in the collection.
 */
export function jobHealth(
  spec: JobSpec,
  latest: JobRun | null,
  now: Date,
  /** What the last deploy put in the image, from ops/timingImage. */
  deployedBuild?: string | null,
): JobHealth {
  const out = (state: JobState, detail: string): JobHealth => ({
    spec,
    state,
    bad: BAD.includes(state),
    detail,
    latest,
  })

  if (!latest) {
    return spec.kind === 'scheduled'
      ? out('never', 'Has never run. Check the schedule exists and can invoke the job.')
      : out('idle', 'Nothing has needed it.')
  }

  const sinceBeat = (now.getTime() - latest.heartbeatAt.getTime()) / 1000

  if (latest.status === 'running') {
    const beat = spec.heartbeatSeconds ?? 60
    if (sinceBeat > beat * stalledAfter) {
      return out('stalled', `Claims to be running but stopped reporting ${ago(sinceBeat)} ago.`)
    }
    /*
      Running, but possibly not the code that is deployed.

      A job pins its image when the execution starts, so one that began before
      the last deploy is still running the previous build and will keep doing so
      until it ends. That is not a fault — it is how Cloud Run works — but it is
      invisible, and it has twice made a deployed fix look as though it had not
      worked. Reported as a note on a healthy job rather than as a problem.
    */
    const stale = deployedBuild && latest.build && latest.build !== deployedBuild
    const note = stale ? ' Started before the last deploy, so it is running the previous build.' : ''
    return out('running', (latest.summary || 'Running.') + note)
  }

  if (latest.status === 'failed') {
    return out('failed', latest.error || latest.summary || 'The last run failed.')
  }

  const cadence = intervalSeconds(spec.schedule)
  if (spec.kind === 'scheduled' && cadence) {
    const sinceStart = (now.getTime() - latest.startedAt.getTime()) / 1000
    if (sinceStart > cadence * lateAfter) {
      return out('late', `Last ran ${ago(sinceStart)} ago, and is expected every ${ago(cadence)}.`)
    }
  }

  return out('ok', latest.summary || `Last ran ${ago(sinceBeat)} ago.`)
}

/** Judges the whole fleet, in declared order. */
export function fleetHealth(
  runs: Map<string, JobRun>,
  now: Date,
  jobs: JobSpec[] = JOBS,
  deployedBuild?: string | null,
): JobHealth[] {
  return jobs.map((spec) => jobHealth(spec, runs.get(spec.id) ?? null, now, deployedBuild))
}

/**
 * Whether a run is executing something other than what is deployed.
 *
 * Exposed so a page can mark the run itself, not only the job's summary line —
 * a history of runs is where somebody looks when asking "was that fix in yet",
 * and it is a poor place to have to work it out from timestamps.
 */
export function isStaleBuild(run: JobRun | null | undefined, deployedBuild?: string | null): boolean {
  return !!deployedBuild && !!run?.build && run.build !== deployedBuild
}

/**
 * A duration, rounded to whatever unit makes it readable.
 *
 * Coarse on purpose. "47 minutes" and "46 minutes" mean the same thing to
 * somebody deciding whether to worry, and precision here reads as noise.
 */
export function ago(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 90) return `${s}s`
  const m = Math.round(s / 60)
  if (m < 90) return `${m} min`
  const h = Math.round(m / 60)
  if (h < 48) return `${h} hr`
  return `${Math.round(h / 24)} days`
}
